import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Menu, X, Crown, Heart, Home, BookOpen, Palette, Users, Pencil,
  CalendarDays, GraduationCap, Camera, Mail, Sparkles, BookMarked, Compass,
  User, LogOut, LayoutDashboard, ShoppingBag, Image as ImageIcon, Settings, ChevronDown, ShoppingCart, Tv, Plane, Stamp,
} from "lucide-react";
import { NAV_LINKS } from "../lib/content";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import { api } from "../lib/api";
import CartDrawer from "./CartDrawer";
import MembershipBadge from "./MembershipBadge";

const ICONS = { Home, BookOpen, Palette, Users, Heart, Pencil, CalendarDays, GraduationCap, Camera, Crown, Mail, Sparkles, BookMarked, Compass, Tv, Plane, Stamp };

// Keep the header uncrowded: only business/primary links stay inline; the rest live under "Explore".
const PRIMARY_LABELS = ["Home", "Books", "Watch", "Activities", "Membership"];

const dashFor = (role) => role === "admin" ? { to: "/admin", label: "Admin Dashboard" }
  : role === "educator" ? { to: "/kingdom", label: "Educator Dashboard" }
  : { to: "/kingdom", label: "Parent Dashboard" };

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { count: cartCount, setOpen: setCartOpen } = useCart();
  const menuRef = useRef(null);
  const exploreRef = useRef(null);

  useEffect(() => { setOpen(false); setMenuOpen(false); setExploreOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (exploreRef.current && !exploreRef.current.contains(e.target)) setExploreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") { setMenuOpen(false); setExploreOpen(false); setOpen(false); } };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const [stampsOn, setStampsOn] = useState(false);
  useEffect(() => { api.get("/passport/stamp-gallery/status").then(({ data }) => setStampsOn(data.live || data.preview)).catch(() => {}); }, [user]);
  const STAMPS_LINK = { to: "/adventures/ginger-heart-airlines/stamps", label: "Travel Stamp Collection", icon: "Stamp" };
  const allLinks = stampsOn ? NAV_LINKS.flatMap((l) => (l.label === "SKY Adventure" ? [l, STAMPS_LINK] : [l])) : NAV_LINKS;
  const primaryLinks = allLinks.filter((l) => PRIMARY_LABELS.includes(l.label));
  const exploreLinks = allLinks.filter((l) => !PRIMARY_LABELS.includes(l.label));
  const dash = dashFor(user?.role);
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  const accountItems = [
    { to: dash.to, label: dash.label, icon: LayoutDashboard },
    { to: "/kingdom", label: "Child Profiles", icon: Users },
    { to: "/membership", label: "Membership", icon: Crown },
    { to: "/membership#orders", label: "Orders", icon: ShoppingBag },
    { to: "/showcase", label: "Community Showcase", icon: ImageIcon },
    { to: "/kingdom", label: "Settings", icon: Settings },
  ];

  const doLogout = async () => { await logout(); setMenuOpen(false); setOpen(false); nav("/"); };

  return (
    <>
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_6px_20px_rgba(155,81,224,0.10)]" : "bg-white/60 backdrop-blur-md"}`}>
        <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 h-[74px] flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0 rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2" data-testid="nav-logo">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-goldK text-purpleDark shadow-[0_4px_0_#D4A017] animate-bob">
              <Crown size={24} strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-xl leading-none text-ink">
              Sir Gingerman<span className="block text-[11px] font-body font-semibold tracking-widest text-[#7A3CB3] uppercase">Kingdom</span>
            </span>
          </Link>

          <div className="hidden xl:flex items-center gap-0.5">
            {primaryLinks.map((l) => {
              const Icon = ICONS[l.icon] || Home;
              return (
                <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-1.5 px-3 py-2 rounded-full font-display font-medium text-[15px] whitespace-nowrap transition-colors duration-150 ${
                      isActive ? "bg-purpleK text-white" : "text-ink hover:bg-purpleK/10 hover:text-[#7A3CB3]"} focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2`}>
                  <Icon size={17} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
                  {l.label}
                </NavLink>
              );
            })}
            {/* Explore dropdown — keeps the header uncrowded */}
            <div className="relative" ref={exploreRef}>
              <button onClick={() => setExploreOpen((v) => !v)} data-testid="nav-explore"
                className="group flex items-center gap-1.5 px-3 py-2 rounded-full font-display font-medium text-[15px] text-ink hover:bg-purpleK/10 hover:text-[#7A3CB3] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
                <Compass size={17} strokeWidth={2.5} /> Explore <ChevronDown size={15} className={`transition-transform ${exploreOpen ? "rotate-180" : ""}`} />
              </button>
              {exploreOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-2xl bg-white shadow-xl border border-black/5 p-2 animate-pop grid grid-cols-1 gap-0.5 max-h-[70vh] overflow-y-auto" data-testid="explore-menu">
                  {exploreLinks.map((l) => {
                    const Icon = ICONS[l.icon] || Home;
                    return (
                      <NavLink key={l.to} to={l.to} data-testid={`explore-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-display font-semibold text-sm transition-colors ${isActive ? "bg-purpleK text-white" : "text-ink/80 hover:bg-cream"}`}>
                        <Icon size={17} className="text-purpleK" /> {l.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* account access */}
          <div className="flex items-center gap-2">
            <MembershipBadge className="hidden sm:inline-flex" />
            <button onClick={() => setCartOpen(true)} data-testid="nav-cart" aria-label="Open cart"
              className="relative grid place-items-center h-11 w-11 rounded-2xl bg-white/90 border border-purpleK/25 text-[#7A3CB3] hover:bg-purpleK/15 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
              <ShoppingCart size={21} strokeWidth={2.5} />
              {cartCount > 0 && <span data-testid="cart-count" className="absolute -top-1 -right-1 grid place-items-center min-w-[20px] h-5 px-1 rounded-full bg-pinkK text-white text-[11px] font-display font-extrabold">{cartCount}</span>}
            </button>
            {user === undefined ? null : !user ? (
              <>
                <Link to="/kingdom/login" data-testid="nav-login"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full border-2 border-purpleK/40 bg-white/95 text-[#7A3CB3] font-display font-bold px-4 py-2 hover:bg-purpleK/10 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
                  <User size={17} /> Login
                </Link>
                <Link to="/kingdom/login?mode=signup" data-testid="nav-join-free"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-goldK text-ink font-display font-bold px-5 py-2.5 shadow-[0_5px_0_#D4A017] hover:-translate-y-0.5 active:translate-y-1 transition-transform duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
                  <Sparkles size={17} strokeWidth={2.5} /> Join Free
                </Link>
              </>
            ) : (
              <div className="relative hidden sm:block" ref={menuRef}>
                <button onClick={() => setMenuOpen((v) => !v)} data-testid="nav-account-button"
                  className="inline-flex items-center gap-2 rounded-full bg-purpleK/10 hover:bg-purpleK/20 pl-1.5 pr-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-purpleK text-white font-display font-bold text-sm">{initial}</span>
                  <span className="font-display font-bold text-ink text-sm max-w-[110px] truncate">{user.name || user.email}</span>
                  <ChevronDown size={16} className={`text-ink/50 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-60 rounded-2xl bg-white shadow-xl border border-black/5 p-2 animate-pop" data-testid="account-menu">
                    {accountItems.map((it, i) => (
                      <Link key={i} to={it.to} data-testid={`account-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-display font-semibold text-ink/80 hover:bg-cream transition-colors text-sm">
                        <it.icon size={17} className="text-purpleK" /> {it.label}
                      </Link>
                    ))}
                    <button onClick={doLogout} data-testid="account-sign-out"
                      className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-display font-semibold text-pinkK hover:bg-pinkK/10 transition-colors text-sm mt-1 border-t border-black/5 pt-3">
                      <LogOut size={17} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setOpen((v) => !v)} data-testid="nav-menu-toggle"
              className="xl:hidden grid place-items-center h-11 w-11 rounded-2xl bg-purpleK text-white shadow-[0_4px_0_#7A3CB3] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#7A3CB3]/80 focus-visible:ring-offset-2">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 xl:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute top-[74px] inset-x-3 rounded-3xl bg-white p-4 shadow-2xl animate-pop max-h-[80vh] overflow-y-auto">
            <div className="mb-3"><MembershipBadge full /></div>
            {/* account block (mobile) */}
            {!user ? (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Link to="/kingdom/login" data-testid="mobile-nav-login" className="flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-display font-bold border-2 border-purpleK/25 text-purpleK"><User size={18} /> Login</Link>
                <Link to="/kingdom/login?mode=signup" data-testid="mobile-nav-join-free" className="flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-display font-bold bg-goldK text-ink"><Sparkles size={18} /> Join Free</Link>
              </div>
            ) : (
              <div className="mb-3 rounded-2xl bg-cream p-2" data-testid="mobile-account-menu">
                <div className="flex items-center gap-2 px-2 py-2">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-purpleK text-white font-display font-bold">{initial}</span>
                  <span className="font-display font-bold text-ink text-sm truncate">{user.name || user.email}</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {accountItems.map((it, i) => (
                    <Link key={i} to={it.to} className="flex items-center gap-2 rounded-xl px-3 py-2.5 font-display font-semibold text-ink/80 bg-white text-sm"><it.icon size={16} className="text-purpleK" /> {it.label}</Link>
                  ))}
                </div>
                <button onClick={doLogout} className="w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 font-display font-bold text-pinkK mt-1.5"><LogOut size={16} /> Sign Out</button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {allLinks.map((l) => {
                const Icon = ICONS[l.icon] || Home;
                return (
                  <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-2xl px-4 py-3 font-display font-semibold ${isActive ? "bg-purpleK text-white" : "bg-cream text-ink"}`}>
                    <Icon size={20} strokeWidth={2.5} /> {l.label}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <CartDrawer />
    </>
  );
}
